import React, { useState, useRef, useEffect } from 'react';
import { useWeb3 } from '../context/Web3Context';
import { useCurrency } from '../context/CurrencyContext';
import { ConnectWallet } from './ConnectWallet';
import { api } from '../services/api';
import {
  Loader2, Hexagon, Wallet, FileText, MapPin,
  Shield, Upload, X, CheckCircle, Camera, KeyRound,
  Sliders, Users, Globe, Lock, Eye, Award, Sparkles,
  ArrowRight, ChevronRight, Activity
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
} from './ui/dialog';

const storageTypes = [
  { value: 'climate_controlled', label: 'Climate-Controlled Vault' },
  { value: 'private_garage', label: 'Private Garage' },
  { value: 'wine_cellar', label: 'Wine Cellar' },
  { value: 'safe_deposit', label: 'Safe Deposit Box' },
  { value: 'display_case', label: 'Display Case' },
  { value: 'private_collection', label: 'Private Collection' },
  { value: 'professional_storage', label: 'Professional Storage Facility' },
  { value: 'other', label: 'Other' },
];

const syndicateTiers = [
  { value: 'private_circle', label: 'Private Circle', icon: Lock, desc: 'Invite-only. Exclusive access to vetted connections.' },
  { value: 'vetted_network', label: 'Vetted Network', icon: Eye, desc: 'Verified users on the Assagart platform.' },
  { value: 'global_syndicate', label: 'Global Syndicate', icon: Globe, desc: 'Open marketplace. Maximum liquidity potential.' },
];

export const TokenizeCeremony = ({ open, onOpenChange, asset, onSuccess }) => {
  const { walletAddress } = useWeb3();
  const { formatCurrency } = useCurrency();
  const fileInputRef = useRef(null);

  // Shared state
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [storageLocation, setStorageLocation] = useState('');
  const [storageType, setStorageType] = useState('');
  const [hasInsurance, setHasInsurance] = useState(false);
  const [insuranceDetails, setInsuranceDetails] = useState('');
  const [totalFractions, setTotalFractions] = useState('1000');
  const [pricePerFraction, setPricePerFraction] = useState('');
  const [liquidityPortion, setLiquidityPortion] = useState(20);
  const [syndicateTier, setSyndicateTier] = useState('private_circle');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);

  // Ceremony state
  const [ceremony, setCeremony] = useState('entry'); // entry | strategy | activation | sealing | success
  const [provenanceStep, setProvenanceStep] = useState(0);
  const [sealingProgress, setSealingProgress] = useState(0);

  const assetValue = asset?.current_value || 0;
  const liquidityValue = assetValue * (liquidityPortion / 100);
  const retainedOwnership = 100 - liquidityPortion;

  // Auto-calculate fractions from liquidity portion
  useEffect(() => {
    if (assetValue > 0 && totalFractions) {
      const fracs = parseInt(totalFractions);
      if (fracs > 0) {
        const fractionValue = (assetValue * liquidityPortion / 100) / fracs;
        setPricePerFraction(fractionValue.toFixed(2));
      }
    }
  }, [assetValue, totalFractions, liquidityPortion]);

  // Provenance chain animation
  useEffect(() => {
    if (ceremony === 'entry') {
      const timer = setInterval(() => {
        setProvenanceStep(prev => prev < 3 ? prev + 1 : prev);
      }, 800);
      return () => clearInterval(timer);
    }
  }, [ceremony]);

  // Sealing animation
  useEffect(() => {
    if (ceremony === 'sealing') {
      const timer = setInterval(() => {
        setSealingProgress(prev => {
          if (prev >= 100) {
            clearInterval(timer);
            return 100;
          }
          return prev + 2;
        });
      }, 50);
      return () => clearInterval(timer);
    }
  }, [ceremony]);

  useEffect(() => {
    if (sealingProgress >= 100) {
      const timeout = setTimeout(() => setCeremony('success'), 600);
      return () => clearTimeout(timeout);
    }
  }, [sealingProgress]);

  const handleDocumentUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    for (const file of files) {
      try {
        const response = await api.uploadImage(file);
        setDocuments(prev => [...prev, { name: file.name, url: response.data.url, type: file.type, size: file.size }]);
      } catch { toast.error(`Failed to upload ${file.name}`); }
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeDocument = (index) => setDocuments(prev => prev.filter((_, i) => i !== index));

  const handleActivate = async () => {
    if (!termsAccepted) { toast.error('Please accept the terms'); return; }
    if (!hasInsurance) { toast.error('Insurance is mandatory for tokenization'); return; }
    
    setLoading(true);
    setCeremony('sealing');
    
    try {
      const response = await api.tokenizeAsset({
        asset_id: asset.id,
        total_fractions: parseInt(totalFractions),
        price_per_fraction: parseFloat(pricePerFraction),
        documents: documents.map(d => d.name),
        storage_location: storageLocation,
        storage_type: storageType,
        has_insurance: hasInsurance,
        insurance_details: insuranceDetails,
        terms_accepted: termsAccepted,
        syndicate_tier: syndicateTier,
        liquidity_portion: liquidityPortion,
      });
      
      toast.success('Digital Twin activation submitted!');
      if (onSuccess) onSuccess(response.data);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Activation failed';
      toast.error(msg);
      setCeremony('activation');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCeremony('entry');
    setProvenanceStep(0);
    setSealingProgress(0);
    setDocuments([]);
    setTermsAccepted(false);
    onOpenChange(false);
  };

  const canProceedToStrategy = documents.length > 0 && storageLocation && storageType && hasInsurance;
  const canActivate = termsAccepted && liquidityPortion > 0 && liquidityPortion <= 100;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-[#050505] border-gold/20 max-w-2xl max-h-[90vh] overflow-y-auto p-0" data-testid="tokenize-ceremony">
        
        {/* Ceremony Progress */}
        <div className="px-6 pt-6 pb-2">
          <div className="flex items-center gap-1 mb-1">
            {[
              { key: 'entry', label: 'The Entry' },
              { key: 'strategy', label: 'The Strategy' },
              { key: 'activation', label: 'The Activation' },
            ].map((s, i) => {
              const steps = ['entry', 'strategy', 'activation'];
              const currentIdx = steps.indexOf(ceremony === 'sealing' || ceremony === 'success' ? 'activation' : ceremony);
              const isActive = i === currentIdx;
              const isComplete = i < currentIdx || ceremony === 'success';
              return (
                <React.Fragment key={s.key}>
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs transition-all ${
                    isActive ? 'bg-gold/20 text-gold border border-gold/30' :
                    isComplete ? 'bg-emerald-400/10 text-emerald-400' : 'text-muted-foreground'
                  }`}>
                    {isComplete ? <CheckCircle size={12} /> : <span className="w-4 h-4 rounded-full border border-current flex items-center justify-center text-[9px]">{i + 1}</span>}
                    <span className="hidden sm:inline">{s.label}</span>
                  </div>
                  {i < 2 && <ChevronRight size={14} className="text-[#262626]" />}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* ═══ CLICK 1: THE ENTRY ═══ */}
        {ceremony === 'entry' && (
          <div className="px-6 pb-6 space-y-5 ceremony-fade-in">
            {/* Header */}
            <div className="text-center py-4">
              <div className="inline-flex p-3 bg-gold/10 rounded-full mb-3">
                <KeyRound size={24} className="text-gold" />
              </div>
              <h2 className="font-serif text-2xl text-white mb-1">Asset Verification</h2>
              <p className="text-sm text-muted-foreground">Establish provenance, custody, and eligibility</p>
            </div>

            {/* Provenance Chain */}
            <div className="p-4 bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg" data-testid="provenance-chain">
              <p className="text-[10px] text-gold uppercase tracking-[0.2em] mb-4">Provenance Chain</p>
              <div className="space-y-3">
                {[
                  { label: 'Asset Identity', detail: asset?.name, status: provenanceStep >= 0 },
                  { label: 'Ownership Record', detail: 'Verified via uploaded documentation', status: provenanceStep >= 1 },
                  { label: 'Custody Status', detail: 'Insured stewardship confirmed', status: provenanceStep >= 2 },
                  { label: 'Compliance Oracle', detail: 'Regulatory eligibility cleared', status: provenanceStep >= 3 },
                ].map((item, i) => (
                  <div key={i} className={`flex items-center gap-3 transition-all duration-500 ${item.status ? 'opacity-100' : 'opacity-30'}`}>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                      item.status ? 'bg-emerald-400/20' : 'bg-[#1A1A1A]'
                    }`}>
                      {item.status ? <CheckCircle size={12} className="text-emerald-400" /> : <div className="w-2 h-2 rounded-full bg-[#333]" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-white">{item.label}</p>
                      <p className="text-[10px] text-muted-foreground">{item.detail}</p>
                    </div>
                    {item.status && <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />}
                  </div>
                ))}
              </div>
            </div>

            {/* Asset Card */}
            <div className="flex items-center gap-4 p-4 bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg">
              {asset?.image_url && (
                <img src={asset.image_url} alt={asset.name} className="w-16 h-16 object-cover rounded" />
              )}
              <div className="flex-1">
                <p className="text-white font-serif">{asset?.name}</p>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">{asset?.category}</p>
              </div>
              <div className="text-right">
                <p className="text-gold font-serif text-lg">{formatCurrency(assetValue)}</p>
                <p className="text-[10px] text-muted-foreground">AI Valuation</p>
              </div>
            </div>

            {/* Documents Upload */}
            <div>
              <Label className="text-sm uppercase tracking-wider text-muted-foreground mb-3 block flex items-center gap-1">
                <FileText size={12} /> Proof of Ownership <span className="text-red-400">*</span>
              </Label>
              {documents.length > 0 && (
                <div className="space-y-2 mb-3">
                  {documents.map((doc, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 bg-[#0F0F0F] border border-[#1A1A1A] rounded">
                      <FileText size={14} className="text-gold" />
                      <span className="text-sm text-white flex-1 truncate">{doc.name}</span>
                      <button onClick={() => removeDocument(i)} className="text-muted-foreground hover:text-red-400"><X size={14} /></button>
                    </div>
                  ))}
                </div>
              )}
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border border-dashed border-gold/20 rounded-lg p-5 text-center cursor-pointer hover:border-gold/40 transition-colors"
                data-testid="document-upload-area"
              >
                {uploading ? (
                  <div className="flex flex-col items-center gap-2">
                    <Loader2 size={20} className="text-gold animate-spin" />
                    <p className="text-sm text-muted-foreground">Uploading...</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <Upload size={20} className="text-muted-foreground" />
                    <p className="text-sm text-white">Upload documents</p>
                    <p className="text-xs text-muted-foreground">PDF, images, or certificates</p>
                  </div>
                )}
              </div>
              <input ref={fileInputRef} type="file" accept="image/*,.pdf" multiple onChange={handleDocumentUpload} className="hidden" data-testid="document-file-input" />
            </div>

            {/* Storage & Insurance */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                  <MapPin size={10} /> Storage <span className="text-red-400">*</span>
                </Label>
                <select
                  value={storageType}
                  onChange={(e) => setStorageType(e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-[#1A1A1A] text-white text-sm rounded px-3 py-2.5"
                  data-testid="storage-type-select"
                >
                  <option value="">Select type...</option>
                  {storageTypes.map(st => (<option key={st.value} value={st.value}>{st.label}</option>))}
                </select>
                <Input
                  value={storageLocation}
                  onChange={(e) => setStorageLocation(e.target.value)}
                  placeholder="e.g., Mayfair Vault, London"
                  className="luxury-input"
                  data-testid="storage-location-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                  <Shield size={10} /> Insurance <span className="text-red-400">*</span>
                </Label>
                <label className="flex items-center gap-3 p-3 bg-[#0A0A0A] border border-[#1A1A1A] rounded cursor-pointer hover:border-gold/20 transition-colors">
                  <input type="checkbox" checked={hasInsurance} onChange={(e) => setHasInsurance(e.target.checked)} className="w-4 h-4 accent-gold" data-testid="insurance-checkbox" />
                  <div>
                    <p className="text-sm text-white">Insured <span className="text-red-400">*</span></p>
                    <p className="text-[10px] text-muted-foreground">Mandatory for activation</p>
                  </div>
                </label>
                {hasInsurance && (
                  <div className="space-y-1">
                    <label className="text-[10px] text-muted-foreground uppercase tracking-wider">Policy Number <span className="text-red-400">*</span></label>
                    <Input value={insuranceDetails} onChange={(e) => setInsuranceDetails(e.target.value)} placeholder="e.g. POL-2024-AXA-003892" className="luxury-input" data-testid="insurance-details-input" required />
                    <p className="text-[10px] text-muted-foreground">Your insurance broker will be contacted to verify this policy.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Wallet */}
            {!walletAddress ? (
              <div className="p-3 bg-amber-500/5 border border-amber-500/20 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Wallet size={14} className="text-amber-400" />
                  <p className="text-amber-400 text-xs">Connect wallet to receive the Digital Twin on-chain</p>
                </div>
                <ConnectWallet />
              </div>
            ) : (
              <div className="p-2.5 bg-emerald-500/5 border border-emerald-500/20 rounded-lg flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                <span className="text-emerald-400 text-xs font-mono">{walletAddress.slice(0, 6)}...{walletAddress.slice(-4)}</span>
              </div>
            )}

            {/* Education Overlay */}
            {provenanceStep >= 3 && documents.length > 0 && (
              <div className="p-4 bg-gold/5 border border-gold/20 rounded-lg ceremony-fade-in" data-testid="entry-education">
                <p className="text-sm text-gold leading-relaxed">
                  Your asset is now a liquid instrument. Its value is verified, its safety is guaranteed, and it is ready for activation.
                </p>
              </div>
            )}

            <Button
              onClick={() => setCeremony('strategy')}
              disabled={!canProceedToStrategy}
              className="w-full bg-gold/10 border border-gold/30 text-gold hover:bg-gold/20 py-5"
              data-testid="proceed-to-strategy-btn"
            >
              <KeyRound size={16} className="mr-2" />
              Proceed to Strategy
              <ArrowRight size={16} className="ml-2" />
            </Button>
          </div>
        )}

        {/* ═══ CLICK 2: THE STRATEGY ═══ */}
        {ceremony === 'strategy' && (
          <div className="px-6 pb-6 space-y-5 ceremony-fade-in">
            <div className="text-center py-4">
              <div className="inline-flex p-3 bg-gold/10 rounded-full mb-3">
                <Sliders size={24} className="text-gold" />
              </div>
              <h2 className="font-serif text-2xl text-white mb-1">Bespoke Structuring</h2>
              <p className="text-sm text-muted-foreground">Configure your liquidity and syndicate access</p>
            </div>

            {/* Liquidity Configurator */}
            <div className="p-5 bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg" data-testid="liquidity-configurator">
              <p className="text-[10px] text-gold uppercase tracking-[0.2em] mb-4">Liquidity Portion</p>
              
              <div className="mb-4">
                <input
                  type="range"
                  min="5"
                  max="100"
                  step="5"
                  value={liquidityPortion}
                  onChange={(e) => setLiquidityPortion(parseInt(e.target.value))}
                  className="w-full h-1 bg-[#1A1A1A] rounded-full appearance-none cursor-pointer accent-gold"
                  style={{
                    background: `linear-gradient(to right, #D4AF37 0%, #D4AF37 ${liquidityPortion}%, #1A1A1A ${liquidityPortion}%, #1A1A1A 100%)`
                  }}
                  data-testid="liquidity-slider"
                />
                <div className="flex justify-between mt-2 text-[10px] text-muted-foreground">
                  <span>5%</span>
                  <span className="text-gold font-mono text-sm">{liquidityPortion}%</span>
                  <span>100%</span>
                </div>
              </div>

              {/* Capital Efficiency Display */}
              <div className="grid grid-cols-2 gap-3" data-testid="capital-efficiency">
                <div className="p-3 bg-[#0F0F0F] rounded text-center">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Unlock Liquidity</p>
                  <p className="text-gold font-serif text-lg">{formatCurrency(liquidityValue)}</p>
                </div>
                <div className="p-3 bg-[#0F0F0F] rounded text-center">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Retain Ownership</p>
                  <p className="text-white font-serif text-lg">{retainedOwnership}%</p>
                </div>
              </div>

              <p className="text-xs text-gold/80 mt-3 leading-relaxed">
                Unlock {formatCurrency(liquidityValue)} in liquidity while retaining {retainedOwnership}% ownership and full physical enjoyment rights.
              </p>
            </div>

            {/* Syndicate Tier Selector */}
            <div data-testid="syndicate-selector">
              <p className="text-[10px] text-gold uppercase tracking-[0.2em] mb-3">Syndicate Tier</p>
              <div className="space-y-2">
                {syndicateTiers.map(tier => {
                  const Icon = tier.icon;
                  const isSelected = syndicateTier === tier.value;
                  return (
                    <button
                      key={tier.value}
                      onClick={() => setSyndicateTier(tier.value)}
                      className={`w-full text-left p-4 rounded-lg border transition-all ${
                        isSelected 
                          ? 'bg-gold/10 border-gold/40' 
                          : 'bg-[#0A0A0A] border-[#1A1A1A] hover:border-gold/20'
                      }`}
                      data-testid={`syndicate-${tier.value}`}
                    >
                      <div className="flex items-center gap-3">
                        <Icon size={16} className={isSelected ? 'text-gold' : 'text-muted-foreground'} />
                        <div className="flex-1">
                          <p className={`text-sm ${isSelected ? 'text-gold' : 'text-white'}`}>{tier.label}</p>
                          <p className="text-[10px] text-muted-foreground">{tier.desc}</p>
                        </div>
                        {isSelected && <CheckCircle size={14} className="text-gold" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Fraction Configuration */}
            <div className="p-4 bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg">
              <p className="text-[10px] text-gold uppercase tracking-[0.2em] mb-3">Fraction Configuration</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] text-muted-foreground mb-1">Total Fractions</p>
                  <Input type="number" min="1" max="10000" value={totalFractions} onChange={(e) => setTotalFractions(e.target.value)} className="luxury-input" data-testid="fractions-input" />
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground mb-1">Price Per Fraction</p>
                  <Input type="number" step="0.01" value={pricePerFraction} readOnly className="luxury-input opacity-60" data-testid="price-per-fraction-input" />
                </div>
              </div>
            </div>

            {/* Market Oracle */}
            <div className="p-4 bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg" data-testid="market-oracle">
              <div className="flex items-center gap-2 mb-2">
                <Activity size={12} className="text-emerald-400" />
                <p className="text-[10px] text-emerald-400 uppercase tracking-[0.2em]">Market Oracle Feed</p>
              </div>
              <p className="text-xs text-muted-foreground">
                AI valuation of <span className="text-gold">{formatCurrency(assetValue)}</span> is grounded in current market data, 
                comparable sales, and condition assessment. {liquidityPortion < 50 && 'Conservative liquidity portion maximises retained value.'}
              </p>
            </div>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setCeremony('entry')} className="flex-1 border-[#262626] text-muted-foreground hover:text-white">
                Back
              </Button>
              <Button
                onClick={() => setCeremony('activation')}
                className="flex-1 bg-gold/10 border border-gold/30 text-gold hover:bg-gold/20 py-5"
                data-testid="proceed-to-activation-btn"
              >
                Proceed to Activation
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* ═══ CLICK 3: THE ACTIVATION ═══ */}
        {ceremony === 'activation' && (
          <div className="px-6 pb-6 space-y-5 ceremony-fade-in">
            <div className="text-center py-4">
              <div className="inline-flex p-3 bg-gold/10 rounded-full mb-3">
                <Sparkles size={24} className="text-gold" />
              </div>
              <h2 className="font-serif text-2xl text-white mb-1">Global Liquidity Launch</h2>
              <p className="text-sm text-muted-foreground">Review and activate your Digital Twin</p>
            </div>

            {/* Summary Card */}
            <div className="p-5 bg-[#0A0A0A] border border-gold/20 rounded-lg space-y-3" data-testid="activation-summary">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Asset</span>
                <span className="text-white font-serif">{asset?.name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Valuation</span>
                <span className="text-gold">{formatCurrency(assetValue)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Liquidity Portion</span>
                <span className="text-white">{liquidityPortion}% ({formatCurrency(liquidityValue)})</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Retained Ownership</span>
                <span className="text-emerald-400">{retainedOwnership}%</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Syndicate</span>
                <span className="text-white">{syndicateTiers.find(t => t.value === syndicateTier)?.label}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Fractions</span>
                <span className="text-white">{totalFractions} @ {formatCurrency(parseFloat(pricePerFraction) || 0)} each</span>
              </div>
              <div className="border-t border-gold/10 pt-2 flex justify-between text-sm">
                <span className="text-muted-foreground">Insurance</span>
                <span className="text-emerald-400">{hasInsurance ? 'Active' : 'None'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Documents</span>
                <span className="text-emerald-400">{documents.length} verified</span>
              </div>
            </div>

            {/* Terms */}
            <div className="p-4 bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg text-xs text-muted-foreground space-y-1.5">
              <p className="text-white text-sm font-medium mb-2">Terms of Activation</p>
              <p>1. You are the legal owner and have the right to tokenize this asset.</p>
              <p>2. All documentation is accurate and authentic.</p>
              <p>3. The asset will remain in declared custody with active insurance.</p>
              <p>4. Fraction holders have a proportional claim to the asset's value.</p>
              <p>5. Assagart reserves the right to freeze the Digital Twin if terms are breached.</p>
            </div>

            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} className="w-4 h-4 mt-0.5 accent-gold" data-testid="terms-checkbox" />
              <span className="text-sm text-muted-foreground">I accept the Terms of Activation and confirm all information is accurate.</span>
            </label>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setCeremony('strategy')} className="flex-1 border-[#262626] text-muted-foreground hover:text-white">
                Back
              </Button>
              <Button
                onClick={handleActivate}
                disabled={loading || !canActivate}
                className="flex-1 relative overflow-hidden bg-gradient-to-r from-gold/80 to-gold text-black font-semibold hover:from-gold hover:to-gold/90 py-5"
                data-testid="activate-btn"
              >
                <Hexagon size={16} className="mr-2" />
                Activate Digital Twin
              </Button>
            </div>
          </div>
        )}

        {/* ═══ SEALING ANIMATION ═══ */}
        {ceremony === 'sealing' && (
          <div className="px-6 pb-6 ceremony-fade-in">
            <div className="flex flex-col items-center justify-center py-12">
              {/* Wax Seal Animation */}
              <div className="relative w-32 h-32 mb-8">
                <div 
                  className="absolute inset-0 rounded-full border-2 border-gold/30"
                  style={{
                    background: `conic-gradient(#D4AF37 ${sealingProgress * 3.6}deg, transparent ${sealingProgress * 3.6}deg)`,
                    opacity: 0.3,
                  }}
                />
                <div className="absolute inset-2 rounded-full bg-[#0A0A0A] flex items-center justify-center">
                  <div className={`transition-all duration-500 ${sealingProgress >= 100 ? 'scale-110' : 'scale-100'}`}>
                    <Award size={40} className={`${sealingProgress >= 100 ? 'text-gold' : 'text-gold/50'} transition-colors`} />
                  </div>
                </div>
              </div>
              
              <h3 className="font-serif text-xl text-white mb-2">Sealing Certificate</h3>
              <p className="text-sm text-muted-foreground mb-4">
                {sealingProgress < 50 ? 'Generating Digital Twin...' : 
                 sealingProgress < 100 ? 'Anchoring to blockchain...' : 'Activation complete'}
              </p>
              <div className="w-48 h-1 bg-[#1A1A1A] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gold rounded-full transition-all duration-100"
                  style={{ width: `${sealingProgress}%` }}
                />
              </div>
            </div>
          </div>
        )}

        {/* ═══ SUCCESS / CERTIFICATE ═══ */}
        {ceremony === 'success' && (
          <div className="px-6 pb-6 ceremony-fade-in">
            <div className="text-center py-8">
              {/* Celebration */}
              <div className="relative inline-block mb-6">
                <div className="absolute -inset-4 bg-gold/10 rounded-full blur-xl animate-pulse" />
                <div className="relative w-20 h-20 bg-gold/20 rounded-full flex items-center justify-center border border-gold/40">
                  <Award size={36} className="text-gold" />
                </div>
              </div>

              <h2 className="font-serif text-2xl text-white mb-2">Digital Twin Activated</h2>
              <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
                Your capital is now active. You have successfully diversified your wealth without parting with your collection.
              </p>

              {/* Certificate Card */}
              <div className="p-5 bg-[#0A0A0A] border border-gold/20 rounded-lg text-left mb-6 max-w-sm mx-auto" data-testid="certificate-card">
                <p className="text-[10px] text-gold uppercase tracking-[0.3em] mb-3 text-center">Certificate of Fractional Ownership</p>
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Asset</span>
                    <span className="text-white font-serif">{asset?.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Owner Retention</span>
                    <span className="text-gold">{retainedOwnership}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Syndicate</span>
                    <span className="text-white">{syndicateTiers.find(t => t.value === syndicateTier)?.label}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Status</span>
                    <span className="text-amber-400">Pending Verification</span>
                  </div>
                </div>
              </div>

              <div className="p-3 bg-[#0F0F0F] border border-[#1A1A1A] rounded-lg text-xs text-muted-foreground mb-6 max-w-sm mx-auto space-y-1">
                <p>What happens next:</p>
                <p>1. Our team reviews your documents and asset details</p>
                <p>2. Once approved, your Digital Twin NFT is minted on-chain</p>
                <p>3. The offering goes live to your chosen syndicate</p>
              </div>

              <Button onClick={handleClose} className="btn-primary px-8" data-testid="ceremony-done-btn">
                Done
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
